<template>
  <div class="visits-overview-view" data-cy="visits-overview-view">
    <AssignmentDetailsModal
      v-if="showAssignmentDetailsModal"
      :plantId="selectedVisit?.PlantId"
      :plantName="selectedVisit?.PlantName"
      @closeModal="showAssignmentDetailsModal = false"
    />
    <VisitsOverviewFiltersMenu
      v-if="showFilters"
      :getPropValues="getPropValuesAsync"
      :filters="filters"
      :beginDate="beginDate"
      :endDate="endDate"
      :closedVisitsMode="closedVisitsMode"
      @beginDateChanged="beginDate = $event"
      @endDateChanged="endDate = $event"
      @apply="onFiltersApplied"
      @clear="onFiltersCleared"
      @clearDateRange="onClearDateRange"
      @close="showFilters = false"
    />
    <div class="visits-overview-view__actions">
      <TableSearch
        class="visits-search"
        :value="searchInput"
        placeholder="Search by Id/Company/Plant/Comments"
        @searchSubmitted="onSearchSubmitted"
        data-cy="visits-search"
      />
      <div class="sort-wrapper">
        <DropDownList
          class="sort-dropdown"
          :textField="'text'"
          :data-items="sortOptions"
          v-model="selectedSortOption"
          @change="onSortChanged"
          data-cy="sort-dropdown"
        />
        <div class="sort-dir" @click="sortAscending = !sortAscending">
          <SortIcon
            :ascending="sortAscending"
            fill="grey"
            activeFill="#399CD3"
          />
        </div>
      </div>
      <div
        class="filters-button"
        @click="onShowFiltersClicked"
        data-cy="filters-button"
      >
        <BadgeContainer
          v-if="filtersCount"
          content="content"
          rounded="full"
          size="small"
        >
          <font-awesome-icon icon="fa-solid fa-filter" />
          <template v-slot:content>
            <span>{{ filtersCount }} </span>
          </template>
        </BadgeContainer>
        <font-awesome-icon v-else icon="fa-solid fa-filter" />
      </div>

      <div class="options-button" @click="showOptions = !showOptions">
        <font-awesome-icon icon="fa-solid fa-sliders" />
        <font-awesome-icon
          class="options-button__arrow"
          :icon="showOptions ? 'chevron-up' : 'chevron-down'"
        />
      </div>

      <div v-if="showOptions" class="options-menu">
        <font-awesome-icon
          icon="times"
          class="options-menu__close"
          @click="showOptions = false"
        />
        <ToggleSwitch
          v-if="!isMobile()"
          class="options-menu__toggle"
          v-model="inlineView"
          text="Inline View"
        />
        <ToggleSwitch
          v-if="isGroupLeader"
          class="options-menu__toggle"
          v-model="includeGroupMembersVisits"
          text="Show Group Members Visits"
        />
        <ToggleSwitch
          class="options-menu__toggle"
          v-model="includeEmptyVisits"
          text="Include Visits without Issues/Comments"
        />
        <ToggleSwitch
          class="options-menu__toggle"
          v-model="closedVisitsMode"
          text="Include Closed Visits"
        />
      </div>
    </div>
    <div v-if="inlineView" class="inline-view">
      <PanelResizer :size="sidebarWidth" class="inline-view__sidebar">
        <VisitsOverviewList
          class="visits-overview-view__list"
          :visits="visits"
          :totalVisits="total"
          :defaultTake="take"
          :skip="skip"
          :loading="loading"
          :selectedVisit="inlineVisit"
          @visitsChanged="onVisitsChanged"
          @visitClicked="onVisitClicked"
          @visitSelected="onVisitSelected"
          @groupPlantClicked="onGroupPlantClicked"
        />
      </PanelResizer>

      <div class="inline-view__report">
        <VisitInspectionForm
          v-if="inlineVisit"
          :key="inlineVisit.Id"
          mode="view"
          :visitId="inlineVisit.Id.toString()"
          :type="EnumVisitMode.Inspection"
          :inlineMode="true"
          @reopened="resetInlineVisit"
          @reviewedClosed="resetInlineVisit"
          @submitted="resetInlineVisit"
        />

        <div v-else class="inline-view__report-no-results">
          <font-awesome-icon
            class="no-results-icon"
            icon="fa-solid fa-clipboard-list"
          />
          <div class="no-results">No EIR Selected</div>
          <p>Please select a visit from the sidebar to view details.</p>
        </div>
      </div>
    </div>
    <VisitsOverviewList
      v-else
      class="visits-overview-view__list"
      :visits="visits"
      :totalVisits="total"
      :defaultTake="take"
      :skip="skip"
      :loading="loading"
      @visitsChanged="onVisitsChanged"
      @visitClicked="onVisitClicked"
      @groupPlantClicked="onGroupPlantClicked"
    />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import type { GetVisitsOverviewForRcWithFilteringRequest } from "@/api/rfrVisitsSearch";
import type { VisitDisplay } from "@/Domain/VisitDisplay";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import {
  FilterValuesTake,
  valueFilterConvert,
} from "@/shared/helpers/filtersHelper";
import { SessionCacheService } from "@/shared/services/SessionCacheService";
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  watch,
  watchEffect,
} from "vue";
import VisitsOverviewList from "./VisitsOverviewList.vue";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import SortIcon from "@/icons/SortIcon.vue";
import { BadgeContainer } from "@progress/kendo-vue-indicators";
import type { FilterMenuItem } from "@/Domain/FilterMenuItem";
import VisitsOverviewFiltersMenu from "./VisitsOverviewFiltersMenu.vue";
import type { FilterExpression } from "@/Domain/FilterExpression";
import type { FilterGroup } from "@/Domain/FilterGroup";
import type { FilterField } from "@/Domain/FilterField";
import { EnumFilterStatementConnector } from "@/Domain/EnumFilterStatementConnector";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import { EnumVisitType } from "@/Domain/EnumVisitType";
import enumHelper from "@/shared/helpers/enumHelper";
import ToggleSwitch from "@/shared/ui/switch/ToggleSwitch.vue";
import AssignmentDetailsModal from "@/views/rfrAssignments/AssignmentDetailsModal.vue";
import { useMainStore } from "@/stores/useMainStore";
import { UserRoles } from "@/Domain/UserRoles";
import VisitInspectionForm from "../VisitInspectionForm/VisitInspectionForm.vue";
import { EnumVisitMode } from "@/Domain/EnumVisitMode";
import { isMobile } from "@/shared/helpers/mobileHelper";
import PanelResizer from "@/shared/ui/resizers/PanelResizer.vue";
import moment from "moment";

const main = useMainStore();

const visits = ref<VisitDisplay[]>([]);
const total = ref<number>(0);
const skip = ref<number>(0);
const take = ref<number>(25);
const loading = ref<boolean>(false);
const showAssignmentDetailsModal = ref<boolean>(false);
const selectedVisit = ref<VisitDisplay | null>(null);
const showOptions = ref<boolean>(false);

const cachePrefix = "VisitsOverview_";

const sortAscending = ref<boolean>(
  SessionCacheService.get(`${cachePrefix}sortAscending`) ?? false
);
const filters = ref<FilterMenuItem[]>(
  SessionCacheService.get(`${cachePrefix}filters`) ?? []
);
const showFilters = ref<boolean>(false);

const searchInput = ref<string>(
  SessionCacheService.get(`${cachePrefix}searchInput`) ?? ""
);
const includeGroupMembersVisits = ref<boolean>(
  SessionCacheService.get(`${cachePrefix}includeGroupMembersVisits`) ?? true
);
const includeEmptyVisits = ref<boolean>(
  SessionCacheService.get(`${cachePrefix}includeEmptyVisits`) ?? false
);
const closedVisitsMode = ref<boolean>(
  SessionCacheService.get(`${cachePrefix}closedVisitsMode`) ?? false
);

const defaultBeginDate = moment().subtract(1, "year").toDate();
const defaultEndDate = new Date();
const beginDate = ref<Date>(
  SessionCacheService.has(`${cachePrefix}beginDate`)
    ? new Date(SessionCacheService.get(`${cachePrefix}beginDate`))
    : defaultBeginDate
);
const endDate = ref<Date>(
  SessionCacheService.has(`${cachePrefix}endDate`)
    ? new Date(SessionCacheService.get(`${cachePrefix}endDate`))
    : defaultEndDate
);

// Only allow inlineView on non-mobile devices
// TODO: add support for mobile devices?
const inlineView = ref<boolean>(
  !isMobile() && SessionCacheService.get(`${cachePrefix}inlineView`, () => true)
);
const inlineVisit = ref<VisitDisplay>();

const sidebarWidth = ref(window.innerWidth * 0.67); // Set initial width to 2/3 of the window width

const sortOptions = [
  {
    text: "Sort By",
    value: "",
  },
  {
    text: "Inspection Date",
    value: "ActualVisitDate",
  },
  {
    text: "Visit Type",
    value: "Type",
  },
  {
    text: "Visit Status",
    value: "ReportStatus",
  },
  {
    text: "RC",
    value: "Rc",
  },
  {
    text: "RFR Name",
    value: "RfrName",
  },
  {
    text: "Plant Name",
    value: "PlantName",
  },
  {
    text: "Company Name",
    value: "Company",
  },
];

const selectedSortOption = ref<{ text: string; value: string }>(
  SessionCacheService.get(`${cachePrefix}sortOption`) ?? sortOptions[0]
);

const valueFiltersConvertOptions = [
  {
    column: "Type",
    convert: (value: number) => {
      return enumHelper.splitByCapitalLetter(EnumVisitType[value]);
    },
  },
] as ValueFieldConvertOption[];

const filterConvertOptions = [
  {
    field: "UrgentActionReq",
    getFilterValue: (value: string) => {
      return value === "Yes" ? true : false;
    },
  },
  {
    field: "Type",
    getFilterValue: (value: string) => {
      const key = enumHelper.joinByCapitalLetter(EnumVisitType, value);
      return EnumVisitType[key as keyof typeof EnumVisitType];
    },
  },
];

const filtersCount = computed(() => {
  let count = filters.value.length;
  if (closedVisitsMode.value) {
    count +=
      beginDate.value.toLocaleDateString("en-US") !==
      defaultBeginDate.toLocaleDateString("en-US")
        ? 1
        : 0;
    count +=
      endDate.value.toLocaleDateString("en-US") !==
      defaultEndDate.toLocaleDateString("en-US")
        ? 1
        : 0;
  }
  return count;
});

const isGroupLeader = computed(() => {
  return main.isInRole(UserRoles.ROLE_GROUP_LEADER);
});

const getVisitsOverviewRequestParameters = function (
  valuesForFieldOnly: string = ""
) {
  const filterModel = {
    FilterGroups: [],
    BooleanLogicOperator: EnumFilterStatementConnector.And,
  } as FilterExpression;

  filters.value.forEach((f) => {
    const filterGroup = {
      FilterFields: [],
      BooleanLogicOperator: EnumFilterStatementConnector.And,
      FieldName: f.field,
      InternalFieldName: "",
    } as FilterGroup;
    const filterField = {
      Operator: "eq",
      Value:
        filterConvertOptions
          .find((item) => item.field === f.field)
          ?.getFilterValue(f.value) ?? f.value,
    } as FilterField;
    filterGroup.FilterFields.push(filterField);

    filterModel.FilterGroups.push(filterGroup);
  });

  const requestParams = {
    userLogin: main.userLogin, // keep userLogin for cache key
    searchValue: searchInput.value,
    filters: JSON.stringify(filterModel),
    skip: skip.value,
    take: take.value,
    valuesForFieldOnly: valuesForFieldOnly,
    sortField: selectedSortOption.value.value,
    ascending: sortAscending.value,
    excludeEmptyVisits: !includeEmptyVisits.value,
    includeGroupMembersVisits: includeGroupMembersVisits.value,
    closedVisits: closedVisitsMode.value,
  } as GetVisitsOverviewForRcWithFilteringRequest;

  if (closedVisitsMode.value) {
    requestParams.beginDate = beginDate.value.toLocaleDateString("en-US");
    requestParams.endDate = endDate.value.toLocaleDateString("en-US");
  }

  return requestParams;
};

const getVisitsOverviewWithFilteringAsync = async function (
  valuesForFieldOnly: string = ""
) {
  const requestParams = getVisitsOverviewRequestParameters(valuesForFieldOnly);
  if (!valuesForFieldOnly)
    return api.get("GetVisitsOverviewForRcWithFiltering", requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return SessionCacheService.getAsync(createCacheKey(requestParams), () =>
    api.get("GetVisitsOverviewForRcWithFiltering", requestParams)
  );
};

const getVisitsOverviewCountAsync = async function () {
  if (loading.value) return;

  total.value = visits.value.length;
  const requestParams = getVisitsOverviewRequestParameters();
  await handleResponsePromiseAsync(
    api.get("GetVisitsOverviewForRcCount", requestParams),
    (data) => {
      total.value = data;
    },
    "VisitsOverviewView.vue",
    "getVisitsOverviewCountAsync"
  );
};

const getVisitsOverviewAsync = async function (append: boolean = false) {
  if (loading.value) return;
  loading.value = true;
  if (!append) visits.value = [];

  await handleResponsePromiseAsync(
    getVisitsOverviewWithFilteringAsync(),
    (data) => {
      if (!append) visits.value = data.ListItems;
      else visits.value = [...visits.value, ...data.ListItems];
    },
    "VisitsOverviewView.vue",
    "getVisitsOverviewAsync"
  );

  loading.value = false;
};

const getPropValuesAsync = async function (field: string) {
  let result: VisitDisplay[] = [];
  await handleResponsePromiseAsync(
    getVisitsOverviewWithFilteringAsync(field),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, field, result);
    },
    "VisitsOverviewView.vue",
    "getPropValuesAsync"
  );
  return result;
};

const onVisitsChanged = function (skipValue: number, takeValue: number) {
  take.value = takeValue;
  skip.value = skipValue;
  getVisitsOverviewAsync(true);
};

const onFiltersApplied = async function (filtersValue: FilterMenuItem[]) {
  filters.value = filtersValue;
  skip.value = 0;
  SessionCacheService.set(`${cachePrefix}filters`, filtersValue);
  await getVisitsOverviewAsync();
  getVisitsOverviewCountAsync();
};

const onFiltersCleared = async function () {
  filters.value = [];
  skip.value = 0;
  SessionCacheService.set(`${cachePrefix}filters`, []);
  await getVisitsOverviewAsync();
  getVisitsOverviewCountAsync();
};

const onClearDateRange = async function () {
  beginDate.value = defaultBeginDate;
  endDate.value = defaultEndDate;
  await getVisitsOverviewAsync();
  getVisitsOverviewCountAsync();
};

const onVisitClicked = function (visitId: number) {
  window.open(
    `RfrVisitInspectionForm?category=Inspection&mode=new&visitID=${visitId}&menu=VisitsAsRc`,
    "_blank",
    "noopener,noreferrer"
  );
};

const onVisitSelected = function (visit: VisitDisplay) {
  inlineVisit.value = visit;
};

const onGroupPlantClicked = function (visit: VisitDisplay) {
  selectedVisit.value = visit;
  showAssignmentDetailsModal.value = true;
};

const onShowFiltersClicked = function () {
  showFilters.value = true;
  showOptions.value = false;
};

const resetInlineVisit = function () {
  inlineVisit.value = undefined;
};

const onSortChanged = function () {
  skip.value = 0;
  SessionCacheService.set(`${cachePrefix}sortAscending`, sortAscending.value);
  SessionCacheService.set(`${cachePrefix}sortOption`, selectedSortOption.value);
  getVisitsOverviewAsync();
};

const onSearchSubmitted = async function (searchValue: string) {
  searchInput.value = searchValue;
  skip.value = 0;
  await getVisitsOverviewAsync();
  await getVisitsOverviewCountAsync();
};

watch(
  () => [sortAscending.value],
  () => {
    onSortChanged();
  }
);

watch(
  () => [
    includeEmptyVisits.value,
    includeGroupMembersVisits.value,
    closedVisitsMode.value,
  ],
  async () => {
    skip.value = 0;
    SessionCacheService.set(
      `${cachePrefix}includeEmptyVisits`,
      includeEmptyVisits.value
    );
    SessionCacheService.set(
      `${cachePrefix}includeGroupMembersVisits`,
      includeGroupMembersVisits.value
    );
    SessionCacheService.set(
      `${cachePrefix}closedVisitsMode`,
      closedVisitsMode.value
    );
    await getVisitsOverviewAsync();
    await getVisitsOverviewCountAsync();
  }
);

watch(
  () => [beginDate.value, endDate.value],
  () => {
    SessionCacheService.set(`${cachePrefix}beginDate`, beginDate.value);
    SessionCacheService.set(`${cachePrefix}endDate`, endDate.value);
  }
);

watchEffect(() => {
  SessionCacheService.set(`${cachePrefix}inlineView`, inlineView.value);
});

watchEffect(() => {
  SessionCacheService.set(`${cachePrefix}searchInput`, searchInput.value);
});

const handleResize = () => {
  // Auto-disable inline view on small screens
  if (window.innerWidth < 768 && inlineView.value) {
    inlineView.value = false;
  }
};

onBeforeMount(async () => {
  window.addEventListener("resize", handleResize);

  await getVisitsOverviewAsync();
  getVisitsOverviewCountAsync();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});
</script>

<style lang="scss">
.visits-overview-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1 1 100%;
  overflow: hidden;
  position: relative;

  &__list {
    margin: 0;
    height: 100%;

    @include media-breakpoint-up(sm) {
      margin: 0 auto;
    }
  }

  &__actions {
    display: flex;
    justify-content: center;
    padding: 12px 0;
    width: 100%;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    position: relative;
    flex-wrap: wrap;

    .sort-wrapper {
      display: flex;
      align-items: center;
      margin-left: 16px;
      max-width: 200px;
      flex: 1;
      gap: 8px;

      .sort-dir {
        cursor: pointer;
      }
    }

    .filters-button {
      border-radius: 50%;
      cursor: pointer;
      width: 42px;
      height: 42px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 4px;

      &:hover {
        background-color: #e2e6ea;
      }

      .fa-filter {
        color: #6c757d;
        font-size: 1.25rem;
      }
    }

    .options-button {
      border-radius: 50%;
      cursor: pointer;
      width: 42px;
      height: 42px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 4px;
      color: #6c757d;
      font-size: 1.2rem;
      gap: 2px;

      &__arrow {
        font-size: 0.85rem;
      }

      &:hover {
        background-color: #e2e6ea;
      }
    }

    .options-menu {
      position: absolute;
      top: 100%;
      background-color: white;
      border-radius: 4px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      padding: 16px;
      z-index: 10;

      &__close {
        position: absolute;
        top: 8px;
        right: 8px;
        cursor: pointer;
      }

      &__toggle {
        margin-bottom: 8px;
      }
    }
  }

  .visits-search {
    width: 280px;
  }

  .inline-view {
    display: flex;
    flex: 1 1 100%;
    height: 100%;
    overflow: hidden;

    &__sidebar {
      flex: 0 0 auto;
      overflow: hidden;
      border-right: 1px solid #e0e0e0;
      display: flex;
    }

    &__report {
      flex: 1;
      overflow-y: auto;
      padding: 16px;

      &-no-results {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        text-align: center;

        p {
          color: #6c757d;
          font-size: 1rem;
        }
      }

      .no-results-icon {
        font-size: 3rem;
        color: #3a5da6;
        margin-bottom: 16px;
      }
    }

    &__resizer {
      width: 12px;
      cursor: ew-resize;
      background-color: #e0e0e0;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .no-results {
    display: flex;
    justify-content: center;
    margin: 8px 0;
    font-size: 1.5rem;
    font-weight: 500;
    color: #6c757d;
  }
}
</style>
